import type { WrittenPage } from '../../sections'

/**
 * "Transforming Pain into Power" e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "ebook", "playbook" gibi yerleşik terimler de öyle.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */

/** İngilizce sayfadaki PDF ile aynı dosya — bkz. `content/pages/ebook-transforming-pain-into-power.ts`. */
const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + 'ebc82665-fef9-4e2f-8f12-b1f54703d0a6-Transforming%20Pain%20into%20Power.pdf'

export const ebookTransformingPainIntoPower: WrittenPage = {
  navLabel: 'Transforming Pain into Power',
  title: 'Transforming Pain into Power',
  description:
    'Verimsizlik ve yöneticinin üzerindeki yükün kısır döngüsünü kırın; zorlukları büyüme ve liderlik fırsatına çevirin. Digitopia Impact Labs’in e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Tükenmişlikten atılıma',
      headline: 'Transforming Pain into Power',
      body: [
        'Verimsizliğin ve yöneticinin üzerindeki yükün kısır döngüsünü nasıl kıracağınızı; '
          + 'zorlukları nasıl büyüme ve liderlik fırsatına çevireceğinizi keşfedin.',
      ],
      ctas: [{ label: 'İndirin', href: PDF }],
      photo: {
        src: '/photo/transforming-pain-ebook-artwork.webp',
        alt:
          'E-kitabın kapak görseli: yıldızlı gökyüzüne püsküren bir yanardağ; kraterden yükselen '
          + 'kıvılcımlar ve yamaçlarından akan lav.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Bu e-kitabı neden indirmelisiniz?',
      body: [
        'Yöneticiler çoğu zaman verimsizlik, tükenmişlik ve karmaşık kurumsal dinamiklerle '
          + 'boğuşuyor. Bu e-kitap, bu zorlukları güce çevirmeniz için uygulanabilir içgörüler '
          + 'sunuyor. Şunları nasıl yapacağınıza dair yol gösterim bulacaksınız:',
        'Liderliğin getirdiği baskı sizi geride tutmasın; acı veren noktaları güçlü atılımlara '
          + 'çevirecek araçları edinin.',
      ],
      points: [
        'Kurumsal ataletten kurtulun ve gerçek anlamda değişimi sürükleyin.',
        'Yöneticinin yalnızlığını aşın, güçlü ve birlikte çalışan ekipler kurun.',
        'Bürokrasiyi aşıp etkisi ölçülebilen kararlar alın.',
        'Yetenek konusundaki zorlukları yönetin, dayanıklı ve bağlı bir ekip oluşturun.',
      ],
      ctas: [{ label: 'Playbook’u indirin', href: PDF }],
      photo: {
        src: '/photo/transforming-pain-ebook-covers.webp',
        alt:
          'Transforming Pain into Power’ın iki kopyası, biri diğerinin arkasında, beyaz zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler kazanacaksınız?',
      points: [
        'Uygulanabilir stratejiler: Kurumsal siloları nasıl dağıtacağınızı, bürokrasiyi nasıl '
          + 'aşacağınızı ve karmaşık liderlik zorluklarını özgüvenle nasıl yöneteceğinizi '
          + 'öğrenin.',
        'Liderlik içgörüleri: Yöneticinin üzerindeki yükü aşmayı ve amaç, netlik ve dayanıklılıkla '
          + 'liderlik etmeyi daha derinden kavrayın.',
        'Sahadan vakalar: Başka liderlerin, bu e-kitaptaki ilkeleri ve stratejileri uygulayarak '
          + 'kurumlarını nasıl dönüştürdüğünü görün.',
        'Somut adımlar: Liderlik etkinliğinizi artırmak ve kurumsal başarıyı sürüklemek için '
          + 'hemen uygulayabileceğiniz adımlarla ayrılın.',
      ],
      ctas: [{ label: 'Playbook’u indirin', href: PDF }],
      photo: {
        src: '/photo/transforming-pain-ebook-stack.webp',
        alt:
          'Transforming Pain into Power’ın basılı kopyalarından oluşan bir yığın, yukarıdan '
          + 'görünüm.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Liderliği dönüştüren harekete katılın',
      sub:
        'Verimsizliğin kısır döngüsünü kırıp özgüvenle liderlik etmeye hazır mısınız? E-kitabı '
        + 'bugün indirin, tükenmişlikten atılıma giden yolculuğunuza başlayın.',
      cta: { label: 'Playbook’u indirin', href: PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Dijital olgunluk değerlendirmesi, departmanlarımızı ortak hedefler etrafında '
            + 'harekete geçirmekte kritik bir araç oldu. Bir moderatörün yönlendirmesi ve '
            + 'sunulan rekabet içgörüleriyle ekiplerimiz kendilerini pazar içinde '
            + 'konumlandırabildi ve dönüşüm adımlarının sahipliğini üstlendi. Departmanlar '
            + 'arasında hesap verebilirliği sağladık ve stratejik hizalanmaya ulaştık.',
          source: 'Tarkan Ateşoğlu, Information Technology Group Manager',
          company: 'Lila Kağıt',
          logo: { name: 'Lila Kağıt', src: '/logo/lilakagitcard.png' },
          portrait: { src: '/photo/portrait-tarkan-atesoglu.png', alt: 'Tarkan Ateşoğlu' },
        },
      ],
    },
  ],
}
